<script setup>
import { inject, ref, reactive, computed, useSlots, toRefs } from "vue";

const props = defineProps({
    label: {
        type: String,
        default: "",
    },
    name: {
        type: String,
        default: "",
    },
    isChecked: {
        type: Boolean,
        default: false,
    },
    value: {
        type: [Number, String],
        default: null,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    modelValue: {
        type: [String, Number],
        default: null,
    },
    size: {
        type: String,
        default: "lg",
    },
});

const emit = defineEmits(['update:modelValue'])

// Try to inject group context
const radioGroup = inject("radioGroup", null);
const isDisabled = computed(() => radioGroup ? radioGroup.disabled.value : props.disabled )
const inputName = computed(() => radioGroup ? radioGroup.name : props.name )

// Use group or local state based on context
const isChecked = computed(() => {
    if (radioGroup) {
        return radioGroup.selectedValue.value === props.value;
    }
    return props.isChecked;
});

const handleChange = () => {
    if (!isDisabled.value) {
        if (radioGroup) {
            radioGroup.updateValue(props.value)
        } else {
            emit('update:modelValue', props.value)
        }
    }
};


</script>

<template lang="pug">
label.qmodii-label-radio(:class="`qmodii-label-for-${props.name} qmodii-label-item-${props.label}`")
    input.qmodii-radio(
        :name="inputName",
        type="radio",
        :aria-label="props.label",
        :value="props.value",
        :checked="isChecked",
        :disabled="isDisabled",
        @change="handleChange"
    )
    span.qmodii-radio-button(:class="`qmodii-radio-button-${props.size}`")
    span.qmodii-label-radio-text(aria-hidden="true") {{ props.label }}
</template>

<style lang="stylus">
.qmodii-label-radio
    display inline-flex
    position relative
    align-items center
    gap 8px

    &:has(input:disabled) span 
        color var(--qmodii-placeholder-text)
        cursor auto

    &:focus-within:has(*:focus-visible)
        outline 2px solid var(--qmodii-primary)
        border-radius var(--qmodii-border-radius-sm)
        outline-offset 2px

    @media (hover: hover) and (pointer: fine)
      &:hover .qmodii-radio ~ .qmodii-radio-button
          background-color var(--qmodii-secondary-hover)

    .qmodii-label-radio-text
        cursor pointer

    .qmodii-radio
        position absolute
        opacity 0
        cursor pointer
        height 0
        width 0
            
        &:checked ~ .qmodii-radio-button
            background-color var(--qmodii-secondary)

            &:after
                display block

    .qmodii-radio-button
        position relative
        height 2em
        width 2em
        background-color var(--qmodii-background)
        border-radius 50%
        border 1px solid var(--qmodii-secondary-text)

        &:after
            content ''
            position absolute
            display none
            top 0.7em
            left 0.7em
            width 0.5em
            height 0.5em
            border-radius 50%
            background var(--qmodii-background)

        &.qmodii-radio-button-md
            height 1.5em
            width 1.5em

            &:after
                top 0.5em
                left 0.5em
                width 0.4em
                height 0.4em

        &.qmodii-radio-button-sm
            height 1em
            width 1em

            &:after
                top 0.3em
                left 0.3em
                width 0.3em
                height 0.3em
</style>
